import React from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import InboxDocumentViewer from "../Inbox/components/InboxDocumentViewer";
import { InboxPageWrapper, InboxLayout } from "../Inbox/InboxStyled";
import { isSafeReturnPath } from "../../utils/returnTo";

/**
 * One document, full width, by id — for links that arrive from outside
 * the app. The Salesforce intake console points here for every file a
 * client sends through their upload page (Matthew, 2026-10-19: "When WHG
 * Docs opens up it doesn't go to these documents"). Those files belong to
 * no matter yet, so no matter page or inbox list can show them; this page
 * needs nothing but the id.
 *
 * The viewer is the inbox one: it resolves the file by id through the
 * presigned-URL endpoint and handles media previews and load errors. An
 * optional ?name= gives the toolbar a human label ("Rating Decision
 * Narrative — Dale Whitfield") since the caller knows it and we may not.
 */
const Page = styled(InboxPageWrapper)`
  padding: 10px 10px 0 10px;
  flex-direction: column;
`;

const Layout = styled(InboxLayout)`
  /* The viewer pane takes the whole width when it is the only column. */
  & > section {
    flex: 1;
    min-width: 0;
  }
`;

const BackLink = styled.button`
  border: 0;
  background: none;
  padding: 0 0 8px 2px;
  color: #0c53bb;
  font-family: 'Manrope', sans-serif;
  font-size: 13px;
  cursor: pointer;
  align-self: flex-start;
  &:hover { text-decoration: underline; }
`;

const DocumentPage = () => {
  const { documentId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const name = (searchParams.get("name") || "").trim().slice(0, 160);
  // An in-app caller (the intake file) says where it came from; a link from
  // outside carries no state and gets no back link.
  const returnTo = location.state?.returnTo;
  const returnLabel = (location.state?.returnLabel || "").toString().slice(0, 80);

  return (
    <Page data-testid="document-page">
      {isSafeReturnPath(returnTo) ? (
        <BackLink type="button" onClick={() => navigate(returnTo)} data-testid="document-back">
          ← Back to {returnLabel || "where you were"}
        </BackLink>
      ) : null}
      <Layout>
        <InboxDocumentViewer documentId={documentId || null} documentName={name} />
      </Layout>
    </Page>
  );
};

export default DocumentPage;
